/**
 * DocumentSection Component
 * Main section component for documents and handwritten notes
 * Pattern from VideoSection with two separate lists
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProspectDocuments } from "../../../../../hooks/useProspectDocuments";
import { downloadAndOpenDocument } from "../../../../../utils/file-download";
import { ConfirmModal } from "../../../../../components/ConfirmModal";
import { Button } from "../../../../../components/Button";
import { AddIcon } from "../../../../../assets/AddIcon";
import { DocumentUploadButton } from "./DocumentUploadButton";
import { DocumentUploadModal } from "./DocumentUploadModal";
import { DocumentList } from "./DocumentList";
import { RecruitingNoteViewerModal } from "./RecruitingNoteViewerModal";
import { NotifyStaffModal } from "../../../../../components/NotifyStaffModal";
import { AtBatLogSection } from "../at-bats/AtBatLogSection";
import { getRecruitingNotesByProspect } from "../../../../../services/recruiting-notes";
import { getSafeErrorMessage } from "../../../../../utils/errorHandler";
import type { RecruitingNoteResponse } from "../../../../../types/api";
import "./DocumentSection.css";

interface DocumentSectionProps {
  prospectId: string;
}

const formatNoteDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

export function DocumentSection({ prospectId }: DocumentSectionProps) {
  const navigate = useNavigate();
  const {
    regularDocuments,
    handwrittenNotes,
    loading,
    error,
    refresh,
    deleteDocument,
    getDocumentUrl,
    needsRefresh,
  } = useProspectDocuments(prospectId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<
    string | null
  >(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Recruiting notes — mobile-authored notes surfaced read-only on web.
  const [recruitingNotes, setRecruitingNotes] = useState<RecruitingNoteResponse[]>([]);
  const [recruitingNotesLoading, setRecruitingNotesLoading] = useState(true);
  const [recruitingNotesError, setRecruitingNotesError] = useState<string | null>(null);
  const [viewingNote, setViewingNote] = useState<RecruitingNoteResponse | null>(null);
  const [notifyNote, setNotifyNote] = useState<RecruitingNoteResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRecruitingNotesLoading(true);
    setRecruitingNotesError(null);
    getRecruitingNotesByProspect(prospectId)
      .then((notes) => {
        if (cancelled) return;
        setRecruitingNotes(notes);
      })
      .catch((err) => {
        if (cancelled) return;
        setRecruitingNotesError(
          getSafeErrorMessage(err, "Failed to load recruiting notes"),
        );
      })
      .finally(() => {
        if (!cancelled) setRecruitingNotesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [prospectId]);

  // Handle document click (open in new tab)
  const handleDocumentClick = async (document: {
    id: string;
    contentType: string;
    fileExtension: string;
    title?: string | null;
  }) => {
    setDownloadError(null);
    setDownloadingDocumentId(document.id);

    try {
      const url = await getDocumentUrl(document.id);
      if (!url) {
        throw new Error("Failed to get document URL");
      }

      const fileName = document.title
        ? `${document.title}${document.fileExtension}`
        : `Document${document.fileExtension}`;

      await downloadAndOpenDocument(url, fileName, document.contentType);
    } catch (err) {
      console.error("[DocumentSection] Failed to open document", {
        error: err,
        documentId: document.id,
      });
      setDownloadError(
        `Failed to open document: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
    } finally {
      setDownloadingDocumentId(null);
    }
  };

  // Handle delete request (show confirmation modal)
  const handleDeleteClick = (documentId: string) => {
    setDocumentToDelete(documentId);
  };

  // Handle confirmed deletion
  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;

    setDeleteError(null);
    try {
      await deleteDocument(documentToDelete);
      setDocumentToDelete(null);
    } catch (err) {
      setDeleteError(
        `Failed to delete document: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
      setDocumentToDelete(null);
    }
  };


  return (
    <div className="document-section">
      {needsRefresh && (
        <div className="document-section__refresh-banner">
          Refreshing document URLs...
        </div>
      )}

      {error && (
        <div className="document-section__error">
          Failed to load documents: {error.message}
        </div>
      )}

      {deleteError && (
        <div className="document-section__error">
          {deleteError}
          <button
            className="document-section__error-dismiss"
            onClick={() => setDeleteError(null)}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {downloadError && (
        <div className="document-section__error">
          {downloadError}
          <button
            className="document-section__error-dismiss"
            onClick={() => setDownloadError(null)}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {/* Recruiting Notes — listed first so notes are the primary surface here. */}
      <div className="document-section__subsection">
        <div className="document-section__subsection-header">
          <h4 className="document-section__subsection-title">Recruiting Notes</h4>
          <Button
            variant="primary"
            onClick={() =>
              navigate(`/recruiting-notes/new?prospectId=${prospectId}`)
            }
            aria-label="Add recruiting note"
          >
            <AddIcon />
            Add Note
          </Button>
        </div>
        {recruitingNotesError && (
          <div className="document-section__error">
            {recruitingNotesError}
          </div>
        )}
        {recruitingNotesLoading ? (
          <div className="recruiting-notes-list__loading">
            Loading recruiting notes...
          </div>
        ) : recruitingNotes.length === 0 ? (
          <div className="recruiting-notes-list__empty">
            <p className="recruiting-notes-list__empty-title">
              No recruiting notes yet
            </p>
            <p className="recruiting-notes-list__empty-subtext">
              Recruiting notes are created on mobile devices
            </p>
          </div>
        ) : (
          <ul className="recruiting-notes-list">
            {recruitingNotes.map((note) => (
              <li key={note.id}>
                <button
                  type="button"
                  className="recruiting-notes-list__item"
                  onClick={() => setViewingNote(note)}
                >
                  <div className="recruiting-notes-list__icon" aria-hidden>
                    {note.noteType === "handwritten" ? "✎" : "📝"}
                  </div>
                  <div className="recruiting-notes-list__content">
                    {note.title && (
                      <div className="recruiting-notes-list__title">
                        {note.title}
                      </div>
                    )}
                    {note.noteType === "handwritten" ? (
                      <div className="recruiting-notes-list__preview recruiting-notes-list__preview--handwritten">
                        Click to view handwritten note
                      </div>
                    ) : (
                      <div className="recruiting-notes-list__preview">
                        {note.content || "Empty note"}
                      </div>
                    )}
                    <div className="recruiting-notes-list__meta">
                      {formatNoteDate(note.updatedAt || note.createdAt)}
                      {note.createdByName ? ` · ${note.createdByName}` : ""}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* At-Bat Log — structured child records on the prospect, not a note type.
          Sits under the notes so a coach who was already writing AB results into
          free text finds the dedicated home for them in the same place. */}
      <div className="document-section__subsection">
        <AtBatLogSection prospectId={prospectId} />
      </div>

      {/* Documents Section */}
      <div className="document-section__subsection">
        <div className="document-section__subsection-header">
          <h4 className="document-section__subsection-title">Documents</h4>
          <DocumentUploadButton
            onOpenModal={() => setIsModalOpen(true)}
            disabled={false}
          />
        </div>
        <DocumentList
          documents={regularDocuments}
          loading={loading && regularDocuments.length === 0}
          downloadingDocumentId={downloadingDocumentId}
          onDocumentClick={handleDocumentClick}
          onDeleteClick={handleDeleteClick}
          emptyText="No documents yet"
          emptySubtext="Click 'Add Document' to upload PDF or image files"
        />
      </div>

      {/* Handwritten Notes Section */}
      <div className="document-section__subsection">
        <h4 className="document-section__subsection-title">
          Handwritten Notes
        </h4>
        <DocumentList
          documents={handwrittenNotes}
          loading={loading && handwrittenNotes.length === 0}
          downloadingDocumentId={downloadingDocumentId}
          onDocumentClick={handleDocumentClick}
          onDeleteClick={handleDeleteClick}
          emptyText="No handwritten notes yet"
          emptySubtext="Handwritten notes are created on mobile devices"
        />
      </div>

      {/* Upload Modal */}
      <DocumentUploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUploadComplete={refresh}
        prospectId={prospectId}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={documentToDelete !== null}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Document"
        message="Are you sure you want to delete this document? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      <RecruitingNoteViewerModal
        note={viewingNote}
        onClose={() => setViewingNote(null)}
        onNotify={(note) => {
          setViewingNote(null);
          setNotifyNote(note);
        }}
      />

      <NotifyStaffModal
        isOpen={notifyNote !== null}
        prospectId={notifyNote?.prospectId ?? prospectId}
        itemType="note"
        itemId={notifyNote?.id ?? ""}
        subject={notifyNote?.prospectName}
        onClose={() => setNotifyNote(null)}
      />
    </div>
  );
}
